/**
 * =============================================================================
 * ROASTBREW - DEVICE INFO UTILITY
 * =============================================================================
 *
 * Collects browser and device information for auth requests.
 * The backend uses this for session tracking, device management,
 * and household detection.
 *
 * USAGE:
 * import { getDeviceInfo } from './utils/device';
 * const device = getDeviceInfo();
 *
 * =============================================================================
 */

function generateFingerprint() {
  const components = [
    navigator.userAgent,
    navigator.language,
    window.screen.width + 'x' + window.screen.height,
    window.screen.colorDepth,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 'unknown',
    navigator.deviceMemory || 'unknown',
  ];

  const str = components.join('|');
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }

  return 'web_' + Math.abs(hash).toString(36);
}

function detectDeviceType() {
  const ua = navigator.userAgent.toLowerCase();

  if (
    /ipad/.test(ua) ||
    (/android/.test(ua) && !/mobile/.test(ua)) ||
    /tablet/.test(ua)
  ) {
    return 'tablet';
  }

  if (
    /iphone|ipod|android.*mobile|windows phone|blackberry|opera mini|iemobile/.test(ua)
  ) {
    return 'mobile';
  }

  return 'desktop';
}

function detectOS() {
  const ua = navigator.userAgent;

  const patterns = [
    { name: 'Windows', regex: /Windows NT (\d+\.?\d*)/ },
    { name: 'macOS', regex: /Mac OS X (\d+[._]\d+[._]?\d*)/ },
    { name: 'iOS', regex: /OS (\d+[._]\d+[._]?\d*) like Mac OS X/ },
    { name: 'Android', regex: /Android (\d+\.?\d*\.?\d*)/ },
    { name: 'Chrome OS', regex: /CrOS \w+ (\d+\.?\d*)/ },
    { name: 'Linux', regex: /Linux/ },
  ];

  for (const { name, regex } of patterns) {
    const match = ua.match(regex);
    if (match) {
      const version = (match[1] || '').replace(/_/g, '.');
      return {
        name,
        version: version || 'Unknown',
      };
    }
  }

  return { name: 'Unknown', version: 'Unknown' };
}

function detectBrowser() {
  const ua = navigator.userAgent;

  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('OPR/') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('Firefox/')) return 'Firefox';
  if (ua.includes('Safari/') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Chrome/')) return 'Chrome';

  return 'Unknown';
}

function buildDeviceName() {
  const browser = detectBrowser();
  const os = detectOS();
  return `${browser} on ${os.name}`;
}

let _cachedDeviceInfo = null;

export function getDeviceInfo() {
  if (_cachedDeviceInfo) return _cachedDeviceInfo;

  const os = detectOS();

  _cachedDeviceInfo = {
    fingerprint: generateFingerprint(),
    name: buildDeviceName(),
    type: detectDeviceType(),
    platform: 'web',
    osName: os.name,
    osVersion: os.version,
    appVersion: process.env.REACT_APP_VERSION || '1.0.0',
    model: detectBrowser(),
    manufacturer: 'Unknown',
  };

  return _cachedDeviceInfo;
}
